import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { ExportMenu } from "@/components/dashboard/export-menu";
import { CenterDots } from "@/components/ui/decorative-patterns";
import type { Cell } from "@/lib/export";

export interface Column<T> {
  key: string;
  header: string;
  align?: "left" | "right";
  render: (row: T) => React.ReactNode;
  /** Raw value used for CSV/Excel/PDF export. Columns without it export blank. */
  exportValue?: (row: T) => Cell;
  className?: string;
}

interface BreakdownTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  title?: string;
  /** Optional muted subtitle shown under the title. */
  description?: string;
  empty?: string;
  /** When set, shows CSV/Excel/PDF export controls; used as the file/sheet name. */
  exportName?: string;
  /** Optional controls rendered in the header bar, left of the export menu
   *  (e.g. a search field). */
  actions?: React.ReactNode;
}

/** Generic, design-consistent table used for every breakdown view. */
export function BreakdownTable<T>({ rows, columns, rowKey, title, description, empty = "Geen gegevens voor deze selectie.", exportName, actions }: BreakdownTableProps<T>) {
  const showExport = !!exportName && rows.length > 0;
  const headerBar = title || showExport || actions;
  return (
    <Card className="min-w-0 overflow-hidden">
      {headerBar && (
        <div className="band-header flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          {title ? (
            <div className="min-w-0">
              <h3 className="font-heading text-base font-semibold">{title}</h3>
              {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
            </div>
          ) : <span />}
          {(actions || showExport) && (
            <div className="flex items-center gap-2">
              {actions}
              {showExport && <ExportMenu name={exportName!} columns={columns} rows={rows} />}
            </div>
          )}
        </div>
      )}
      {rows.length === 0 ? (
        <div className="relative overflow-hidden px-5 py-10 text-center text-sm text-muted-foreground">
          <CenterDots />
          <p className="relative">{empty}</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead
                  key={c.key}
                  className={cn(
                    "text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/70",
                    c.align === "right" && "text-right",
                  )}
                >
                  {c.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={rowKey(row)}>
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    className={cn("tabular-nums", c.align === "right" && "text-right", c.className)}
                  >
                    {c.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
